import { fetchCompanyBranchListApi } from "@/api/services/main/company";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import {
  characterCodeOptions,
  dataFormatOptions,
  delimiterOptions,
  ECharacterCode,
  EDataFormat,
  EDelimiter,
  EFirstRowSpecification,
  firstRowSpecificationOptions,
} from "@/features/company/constants/companyBranchExportCsv";
import { useConvertCompanyBranchToCsvData } from "@/features/company/hooks/useConvertCompanyBranchToCsvData";
import { companyBranchExportCsvSchema } from "@/features/company/schema/companyBranchExportCsvSchema";
import { TCompanyBranchExportCsvForm } from "@/features/company/types/companyBranchExportCsvForm";
import { useVisible } from "@/hooks/useVisible";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { downloadJSONByCSV } from "@/utils/export";
import { toNumber } from "@/utils/number";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { useForm } from "react-hook-form";

type TCompanyBranchExportCsvButtonProps = {
  selectedCompanyBranchIds: string[];
};

export const CompanyBranchExportCsvButton: FC<TCompanyBranchExportCsvButtonProps> = ({ selectedCompanyBranchIds }) => {
  const exportCsvModal = useVisible();

  const methods = useForm<TCompanyBranchExportCsvForm>({
    resolver: yupResolver(companyBranchExportCsvSchema),
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: {
      dataFormat: EDataFormat.IROHANA_STANDARD,
      firstRowSpecification: EFirstRowSpecification.JAPANESE_NAME,
      characterCode: ECharacterCode.SHIFT_JIS,
      delimiter: EDelimiter.COMMA,
    },
  });

  const { convertCompanyToCsvData } = useConvertCompanyBranchToCsvData();

  const { control } = methods;

  const onSubmit = async (data: TCompanyBranchExportCsvForm) => {
    try {
      const rs = await fetchCompanyBranchListApi({ ids: selectedCompanyBranchIds.map((id) => toNumber(id)) });
      let customHeader;
      const csvData = convertCompanyToCsvData(rs.data);

      const charset = data.characterCode === ECharacterCode.SHIFT_JIS ? "shift_jis" : "utf-8";

      downloadJSONByCSV(
        csvData,
        `company-branch-${dayjs().valueOf()}.csv`,
        charset,
        {
          prependHeader: data.firstRowSpecification !== EFirstRowSpecification.NONE,
          delimiter: {
            field: data.delimiter === EDelimiter.COMMA ? "," : data.delimiter === EDelimiter.TAB_CHARACTER ? "\t" : undefined,
            wrap: "",
          },
          trimFieldValues: true,
          excelBOM: data.characterCode === ECharacterCode.UTF8_UNICODE_WITH_BOM,
        },
        customHeader,
      );
      methods.reset();
      exportCsvModal.close();
    } catch (error) {
      showError(error);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => exportCsvModal.open()} disabled={selectedCompanyBranchIds.length === 0}>
        CSVエクスポート
      </Button>

      <Modal width={760} title="CSVエクスポート" isOpen={exportCsvModal.visible} enableFooter={false} onClose={exportCsvModal.close}>
        <Typography sx={{ my: "16px" }} component="h2" variant="body14">
          事業所のデータベースをCSVファイルにエクスポートできます。
        </Typography>
        <Box sx={{ border: "1px solid #CACDCD", padding: "24px", borderRadius: "8px" }}>
          <Form methods={methods} onSubmit={onSubmit} schema={companyBranchExportCsvSchema}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <FormField control={control} label="出力件数" required={false}>
                  <Input value={selectedCompanyBranchIds.length} endAdornment="件" clearable={false} readonly />
                </FormField>
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <FormField control={control} name="dataFormat">
                  <Select options={dataFormatOptions} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="firstRowSpecification">
                  <Select options={firstRowSpecificationOptions} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="characterCode">
                  <Select options={characterCodeOptions} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="delimiter">
                  <Select options={delimiterOptions} />
                </FormField>
              </Grid>
            </Grid>
            <Typography sx={{ mt: "24px", mb: "32px" }} component="p" variant="body14">
              Excelでご利用される場合は、文字コードを「Shift-JIS」、区切り文字を「カンマ」としてください。
            </Typography>
            <Stack flexDirection="row" justifyContent="space-between">
              <Button variant="outline" onClick={() => methods.reset()}>
                リセット
              </Button>
              <Button type="submit">変換してダウンロード</Button>
            </Stack>
          </Form>
        </Box>
      </Modal>
    </>
  );
};
